import { TCommunity, TProductCommunity } from "utils/ecommerseTypes";
import PaginationStore, { TPaginationActionData } from "utils/paginationStore";
import { IInvitation } from "./leases";
import { IContactPerson, IOrganization } from "./memberships";
import { IRole } from "./roleManagement";

export type CommunityStaffRoles = "Arenapersonal" | "Sitemanager";

export interface IMailTemplate {
    id: string;
    name: string;
    community_name?: string | null;
    mail_template_type: "main_template" | "community_specific";
    subject: string | null;
    body: string | null;
}

export interface IMailTemplateQueryParams {
    mail_template_type: "community_specific";
}
export interface IMainCommunity {
    uuid: string;
    address: string | null;
    city: string | null;
    community_type: string | null;
    title: string | null;
}

export interface ICommunityStaff extends IContactPerson {
    communities: TCommunity[];
    role: IRole;
    community_staff_role: string;
    main_community: IMainCommunity;
}

export interface ICommunityStaffReactivated {
    re_activated: boolean;
}

export interface ICommunityStaffInvitation extends IInvitation {
    communities: TCommunity[];
    community_staff_role: string;
    main_community: IMainCommunity;
}

export interface INewCommunityStaff {
    communities: string[];
    mainCommunity: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    communityStaffRole: CommunityStaffRoles | "";
}

export interface IEditCommunityStaff {
    communities: string[];
    mainCommunity: string;
    communityStaffRole: CommunityStaffRoles | "";
    role: number;
}

export interface ICostCalculationRun {
    id: number;
    uuid: string;
    created: string;
    offset: number;
    start_date: string;
    changed_last_invoice_dates: boolean;
    file: string;
}

export interface ICreateCostCalculationRun {
    offset: number;
    start_date: string;
    file_name: string;
}

export interface IRepresentative {
    uuid: string;
    id: number;
    full_name: string;
    email: string;
    membership: IOrganization;
}

export interface IRepresentativeQueryParams {
    search?: string;
    is_active?: boolean;
}

export interface IRepresentativeInvitation {
    uuid: string;
    id: number;
    full_name: string;
    email: string;
    membership: IOrganization;
    created: string;
    updated: string;
    activates: string;
    mail_sent: boolean;
}

export interface IRepresentativeInvitationsQueryParams {
    search?: string;
    is_active?: boolean;
}

export interface ICommunityState {
    communityStaffPagination: PaginationStore<ICommunityStaff>;
    communityStaffInvitationPagination: PaginationStore<ICommunityStaffInvitation>;
    communitiesPagination: PaginationStore<TCommunity>;
    productCommunitiesPagination: PaginationStore<TProductCommunity>;
    costCalculationPagination: PaginationStore<ICostCalculationRun>;
    representativePagination: PaginationStore<IRepresentative>;
    representativeInvitationPagination: PaginationStore<IRepresentativeInvitation>;
    mailTemplates: IMailTemplate[];
    isFetchingCommunityStaff: boolean;
    isFetchingCommunityStaffInvitations: boolean;
    isFetchingCommunity: boolean;
    isFetchingCommunities: boolean;
    isFetchingProductCommunities: boolean;
    isFetchingCostCalculations: boolean;
    isAddingCommunityStaff: boolean;
    isEditingCommunityStaff: boolean;
    isDeactivatingCommunityStaff: boolean;
    isAddingCostCalculation: boolean;
    isFetchingRepresentatives: boolean;
    isFetchingRepresentativeInvitations: boolean;
    isFetchingMailTemplates: boolean;
    addCommunityStaff: (communityStaff: INewCommunityStaff) => Promise<ICommunityStaffReactivated>;
    editCommunityStaff: (userId: number, data: IEditCommunityStaff) => Promise<boolean>;
    deactivateCommunityStaff: (userId: number) => Promise<void>;
    addCostCalculation: (data: ICreateCostCalculationRun) => Promise<Blob | null>;
    fetchCommunity: (uuid: string) => Promise<TCommunity>;
    fetchMailTemplate: (uuid: string) => Promise<IMailTemplate | null>;
}

export const initialState: ICommunityState = {
    communityStaffPagination: new PaginationStore<ICommunityStaff>(),
    communityStaffInvitationPagination: new PaginationStore<ICommunityStaffInvitation>(),
    communitiesPagination: new PaginationStore<TCommunity>(),
    productCommunitiesPagination: new PaginationStore<TProductCommunity>(),
    costCalculationPagination: new PaginationStore<ICostCalculationRun>(),
    representativePagination: new PaginationStore<IRepresentative>(),
    representativeInvitationPagination: new PaginationStore<IRepresentativeInvitation>(),
    mailTemplates: [],
    isFetchingCommunityStaff: false,
    isFetchingCommunityStaffInvitations: false,
    isFetchingCommunity: false,
    isFetchingCommunities: false,
    isFetchingProductCommunities: false,
    isFetchingCostCalculations: false,
    isAddingCommunityStaff: false,
    isEditingCommunityStaff: false,
    isDeactivatingCommunityStaff: false,
    isAddingCostCalculation: false,
    isFetchingRepresentatives: false,
    isFetchingRepresentativeInvitations: false,
    isFetchingMailTemplates: false,
    addCommunityStaff: async (communityStaff: INewCommunityStaff) => ({}) as ICommunityStaffReactivated,
    editCommunityStaff: async (userId: number, data: IEditCommunityStaff) => true,
    deactivateCommunityStaff: async (): Promise<void> => {
        return;
    },
    addCostCalculation: async (data: ICreateCostCalculationRun): Promise<Blob | null> => new Blob(),
    fetchCommunity: async (uuid: string) => ({}) as TCommunity,
    fetchMailTemplate: async (uuid: string) => null,
};

export type IAction =
    | { type: "FETCH_COMMUNITY_STAFF" }
    | { type: "FETCH_COMMUNITY_STAFF_SUCCESS"; data: TPaginationActionData<ICommunityStaff> }
    | { type: "FETCH_COMMUNITY_STAFF_FAILURE" }
    | { type: "FETCH_COMMUNITY_STAFF_INVITATIONS" }
    | { type: "FETCH_COMMUNITY_STAFF_INVITATIONS_SUCCESS"; data: TPaginationActionData<ICommunityStaffInvitation> }
    | { type: "FETCH_COMMUNITY_STAFF_INVITATIONS_FAILURE" }
    | { type: "FETCH_COMMUNITY" }
    | { type: "FETCH_COMMUNITY_SUCCESS" }
    | { type: "FETCH_COMMUNITY_FAILURE" }
    | { type: "FETCH_PRODUCT_COMMUNITIES" }
    | { type: "FETCH_PRODUCT_COMMUNITIES_SUCCESS"; data: TPaginationActionData<TCommunity> }
    | { type: "FETCH_PRODUCT_COMMUNITIES_FAILURE" }
    | { type: "FETCH_COMMUNITIES" }
    | { type: "FETCH_COMMUNITIES_SUCCESS"; data: TPaginationActionData<TCommunity> }
    | { type: "FETCH_COMMUNITIES_FAILURE" }
    | { type: "FETCH_COST_CALCULATIONS" }
    | { type: "FETCH_COST_CALCULATIONS_SUCCESS"; data: TPaginationActionData<ICostCalculationRun> }
    | { type: "FETCH_COST_CALCULATIONS_FAILURE" }
    | { type: "FETCH_MAIL_TEMPLATE" }
    | { type: "FETCH_MAIL_TEMPLATE_SUCCESS"; data: IMailTemplate }
    | { type: "FETCH_MAIL_TEMPLATE_FAILURE" }
    | { type: "FETCH_MAIL_TEMPLATES" }
    | { type: "FETCH_MAIL_TEMPLATES_SUCCESS"; data: IMailTemplate[] }
    | { type: "FETCH_MAIL_TEMPLATES_FAILURE" }
    | { type: "ADD_COST_CALCULATION" }
    | { type: "ADD_COST_CALCULATION_SUCCESS" }
    | { type: "ADD_COST_CALCULATION_FAILURE" }
    | { type: "ADD_COMMUNITY_STAFF" }
    | { type: "ADD_COMMUNITY_STAFF_SUCCESS" }
    | { type: "ADD_COMMUNITY_STAFF_FAILURE" }
    | { type: "EDIT_COMMUNITY_STAFF" }
    | { type: "EDIT_COMMUNITY_STAFF_SUCCESS" }
    | { type: "EDIT_COMMUNITY_STAFF_FAILURE" }
    | { type: "DEACTIVATE_COMMUNITY_STAFF" }
    | { type: "DEACTIVATE_COMMUNITY_STAFF_SUCCESS" }
    | { type: "DEACTIVATE_COMMUNITY_STAFF_FAILURE" }
    | { type: "FETCH_REPRESENTATIVE" }
    | { type: "FETCH_REPRESENTATIVE_SUCCESS"; data: TPaginationActionData<IRepresentative> }
    | { type: "FETCH_REPRESENTATIVE_FAILURE" }
    | { type: "FETCH_REPRESENTATIVE_INVITATION" }
    | { type: "FETCH_REPRESENTATIVE_INVITATION_SUCCESS"; data: TPaginationActionData<IRepresentativeInvitation> }
    | { type: "FETCH_REPRESENTATIVE_INVITATION_FAILURE" };

const reducer = (state: ICommunityState, action: IAction): ICommunityState => {
    switch (action.type) {
        case "FETCH_COMMUNITY_STAFF":
            return { ...state, isFetchingCommunityStaff: true };
        case "FETCH_COMMUNITY_STAFF_SUCCESS":
        case "FETCH_COMMUNITY_STAFF_FAILURE":
            return { ...state, isFetchingCommunityStaff: false };

        case "FETCH_COMMUNITY_STAFF_INVITATIONS":
            return { ...state, isFetchingCommunityStaffInvitations: true };
        case "FETCH_COMMUNITY_STAFF_INVITATIONS_SUCCESS":
        case "FETCH_COMMUNITY_STAFF_INVITATIONS_FAILURE":
            return { ...state, isFetchingCommunityStaffInvitations: false };

        case "FETCH_COMMUNITY":
            return { ...state, isFetchingCommunity: true };
        case "FETCH_COMMUNITY_SUCCESS":
        case "FETCH_COMMUNITY_FAILURE":
            return { ...state, isFetchingCommunity: false };

        case "FETCH_PRODUCT_COMMUNITIES":
            return { ...state, isFetchingProductCommunities: true };
        case "FETCH_PRODUCT_COMMUNITIES_SUCCESS":
        case "FETCH_PRODUCT_COMMUNITIES_FAILURE":
            return { ...state, isFetchingProductCommunities: false };

        case "FETCH_COMMUNITIES":
            return { ...state, isFetchingCommunities: true };
        case "FETCH_COMMUNITIES_SUCCESS":
        case "FETCH_COMMUNITIES_FAILURE":
            return { ...state, isFetchingCommunities: false };

        case "FETCH_COST_CALCULATIONS":
            return { ...state, isFetchingCostCalculations: true };
        case "FETCH_COST_CALCULATIONS_SUCCESS":
        case "FETCH_COST_CALCULATIONS_FAILURE":
            return { ...state, isFetchingCostCalculations: false };
        case "FETCH_MAIL_TEMPLATES":
            return { ...state, isFetchingMailTemplates: true };
        case "FETCH_MAIL_TEMPLATES_SUCCESS":
        case "FETCH_MAIL_TEMPLATES_FAILURE":
            return { ...state, isFetchingMailTemplates: false };
        case "ADD_COMMUNITY_STAFF":
            return {
                ...state,
                isAddingCommunityStaff: true,
                isFetchingCommunityStaff: true,
                isFetchingCommunityStaffInvitations: true,
            };
        case "ADD_COMMUNITY_STAFF_SUCCESS":
            const cStaffPagination = state.communityStaffPagination;
            const cStaffInvitationPagination = state.communityStaffInvitationPagination;
            cStaffPagination.reloadPage();
            cStaffInvitationPagination.reloadPage();
            return {
                ...state,
                communityStaffPagination: cStaffPagination,
                communityStaffInvitationPagination: cStaffInvitationPagination,
                isAddingCommunityStaff: false,
                isFetchingCommunityStaff: false,
                isFetchingCommunityStaffInvitations: false,
            };
        case "ADD_COMMUNITY_STAFF_FAILURE":
            return {
                ...state,
                isAddingCommunityStaff: false,
                isFetchingCommunityStaff: false,
                isFetchingCommunityStaffInvitations: false,
            };

        case "EDIT_COMMUNITY_STAFF":
            return {
                ...state,
                isEditingCommunityStaff: true,
                isFetchingCommunityStaff: true,
            };
        case "EDIT_COMMUNITY_STAFF_SUCCESS":
            const communityStaffPag = state.communityStaffPagination;
            communityStaffPag.reloadPage();
            return {
                ...state,
                communityStaffPagination: communityStaffPag,
                isEditingCommunityStaff: false,
                isFetchingCommunityStaff: false,
            };
        case "EDIT_COMMUNITY_STAFF_FAILURE":
            return {
                ...state,
                isEditingCommunityStaff: false,
                isFetchingCommunityStaff: false,
            };

        case "DEACTIVATE_COMMUNITY_STAFF":
            return { ...state, isDeactivatingCommunityStaff: true };
        case "DEACTIVATE_COMMUNITY_STAFF_SUCCESS":
            return { ...state, isDeactivatingCommunityStaff: false };
        case "DEACTIVATE_COMMUNITY_STAFF_FAILURE":
            return { ...state, isDeactivatingCommunityStaff: false };

        case "ADD_COST_CALCULATION":
            return {
                ...state,
                isAddingCostCalculation: true,
                isFetchingCostCalculations: true,
            };
        case "ADD_COST_CALCULATION_SUCCESS":
            const cCalc = state.costCalculationPagination;
            cCalc.reloadPage();
            return {
                ...state,
                costCalculationPagination: cCalc,
                isAddingCostCalculation: false,
                isFetchingCostCalculations: false,
            };
        case "ADD_COST_CALCULATION_FAILURE":
            return {
                ...state,
                isAddingCostCalculation: false,
                isFetchingCostCalculations: false,
            };

        case "FETCH_REPRESENTATIVE":
            return { ...state, isFetchingRepresentatives: true };
        case "FETCH_REPRESENTATIVE_SUCCESS":
        case "FETCH_REPRESENTATIVE_FAILURE":
            return { ...state, isFetchingRepresentatives: false };

        case "FETCH_REPRESENTATIVE_INVITATION":
            return { ...state, isFetchingRepresentativeInvitations: true };
        case "FETCH_REPRESENTATIVE_INVITATION_SUCCESS":
        case "FETCH_REPRESENTATIVE_INVITATION_FAILURE":
            return { ...state, isFetchingRepresentativeInvitations: false };

        default:
            return { ...state };
    }
};

export default reducer;
